import { useEffect } from 'react';
import { type DialerActivityState, claimTabIndicator } from '../../utils/tabActivityIndicator';

/**
 * What the tab should be signalling about the dialer. `off` means this tab has no Device,
 * which is the normal state for every tab except the one holding it.
 */
export type DialerTabActivity = 'off' | 'ready' | 'ringing' | 'in-call';

const IndicatorFor: Record<Exclude<DialerTabActivity, 'off'>, DialerActivityState> = {
	ready: 'dialer-ready',
	ringing: 'dialer-ringing',
	'in-call': 'dialer-in-call',
};

/** One key for the whole page: a tab holds at most one Device, so its claims cannot stack. */
const DIALER_CLAIM = 'aida-dialer';

/**
 * Marks the tab's title and favicon with the dialer's state, so a rep with several tabs
 * open can see from the tab strip which one holds the dialer and whether it is ringing.
 *
 * Page-level singleton — mount it once per route (the focused view and the legacy
 * telephony provider render on disjoint routes, so only one is ever live).
 */
export function useDialerTabIndicator(activity: DialerTabActivity) {
	useEffect(() => {
		claimTabIndicator(DIALER_CLAIM, activity === 'off' ? null : IndicatorFor[activity]);
	}, [activity]);

	// Release on unmount, or a navigated-away tab keeps claiming the dialer is here.
	useEffect(() => () => claimTabIndicator(DIALER_CLAIM, null), []);
}
